import { useState } from 'react';
import classnames from 'classnames';
import { ListItem, ButtonBase, Collapse } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import { alpha } from '@classify/react-core-components/styles';

import SelectContext from './selectContext';
import useParentSelection from './useParentSelection';
import Tooltip from './Tooltip';

const useStyles = makeStyles(
  /* istanbul ignore next */ ({ palette, spacing, typography, transitions }) => {
    const focusedColor = palette.background.alternative;
    const selectedColor = alpha(palette.brand.main, 0.08);
    const focusedSelectedColor = alpha(palette.brand.main, 0.16);

    return {
      root: {
        padding: 0,
        margin: 0,
        display: 'block',
      },
      button: {
        ...typography.body1,
        boxSizing: 'border-box',
        padding: spacing(1, 0, 1, 2),
        width: 'calc(100% - 4px)',
        whiteSpace: 'nowrap',
        display: 'block',
        textAlign: 'left',
        lineHeight: '2rem',
        borderRadius: [[0, 24, 24, 0]],
        color: palette.text.onMain,
        '&:hover': {
          color: 'inherit',
          backgroundColor: focusedColor,
          '&$selected': {
            backgroundColor: focusedSelectedColor,
          },
          '& $rightIcon': {
            color: palette.brand.main,
          },
        },
      },
      expand: {
        transform: 'rotate(0deg)',
        transition: transitions.create('transform', {
          duration: transitions.duration.shortest,
        }),
        position: 'absolute',
        right: 0,
        top: 10,
        marginRight: spacing(1),
        color: palette.text.inactive,
      },
      overline: {
        ...typography.overline,
        padding: spacing(2, 0, 1, 2),
        color: palette.text.inactive,
      },
      rightIcon: {
        marginLeft: spacing(1),
        color: palette.status.inactive,
        verticalAlign: 'text-bottom',
      },

      isSubItem: {
        '& > $button': {
          paddingLeft: spacing(4),
        },
      },
      selected: {
        fontWeight: typography.fontWeightMedium,
        backgroundColor: selectedColor,
      },
      hasSelectedChildren: {
        fontWeight: typography.fontWeightMedium,
      },
      opened: {
        '& $expand': {
          transform: 'rotate(-180deg)',
        },
      },
      collapsable: {
        '& > $button': {
          borderRadius: 0,
        },
      },
      tooltipIcon: {
        marginLeft: spacing(2),
        color: palette.brand.primary,
        verticalAlign: 'text-bottom',
        height: '20px',
        width: '20px',
      },
    };
  },
  { classNamePrefix: 'DrawerMenuItem' }
);

function DrawerMenuItem({
  label = null,
  onClick = () => {},
  selected = false,
  children = null,
  isSubItem = false,
  isOpen,
  overline = null,
  buttonProps = {},
  tooltipProps = null,
  leftIcon: LeftIcon = null,
  rightIcon: RightIcon = null,
  ariaLabel = 'Show more information',
}) {
  const classes = useStyles();
  const isCollapsable = typeof isOpen === 'boolean';

  useParentSelection(selected);
  const [childrenSelections, setChildrenSelections] = useState({});

  const hasSelectedChildren =
    Boolean(children) &&
    Object.values(childrenSelections).some((isChildSelected) => isChildSelected);

  const onChildSelectChange = (childId, isChildSelected) => {
    setChildrenSelections((prevState) => ({
      ...prevState,
      [childId]: isChildSelected,
    }));
  };

  const wrappedChildren = (
    <SelectContext.Provider value={{ setIsChildSelected: onChildSelectChange }}>
      {children}
    </SelectContext.Provider>
  );

  const content = isCollapsable ? (
    <Collapse in={isOpen} timeout="auto">
      {wrappedChildren}
    </Collapse>
  ) : (
    wrappedChildren
  );

  const tooltip = tooltipProps && (
    <Tooltip title={tooltipProps.title} content={tooltipProps.content}>
      <InfoIcon className={classes.tooltipIcon} />
    </Tooltip>
  );

  return (
    <ListItem
      className={classnames(classes.root, {
        [classes.collapsable]: isCollapsable,
        [classes.isSubItem]: isSubItem,
        [classes.opened]: isOpen,
      })}
    >
      {overline && <div className={classes.overline}>{overline}</div>}

      {label && (
        <ButtonBase
          focusRipple
          onClick={onClick}
          component={buttonProps.component || ButtonBase}
          className={classnames(classes.button, {
            [classes.selected]: selected,
            [classes.hasSelectedChildren]: hasSelectedChildren && selected,
          })}
          {...buttonProps}
        >
          <>
            {LeftIcon ? (
              <LeftIcon fontSize="small" sx={{ verticalAlign: 'text-bottom', marginRight: 1 }} />
            ) : null}
            {label}{' '}
            {RightIcon ? <RightIcon fontSize="small" className={classes.rightIcon} /> : null}
            {isCollapsable && <ExpandMoreIcon className={classes.expand} aria-label={ariaLabel} />}
            {tooltip}
          </>
        </ButtonBase>
      )}
      {content}
    </ListItem>
  );
}

export default DrawerMenuItem;
